import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Ajv from 'ajv';

import env from 'configures';
import {
  LP_URL,
  INPUT_URL,
  CONF_URL,
  THANKS_URL,
  ADD_INPUT_URL,
  ADD_CONF_URL,
  ADD_THANKS_URL,
  PAGE_MAP,
  CHOICES_NAME_MAP,
  ERROR_MESSAGES,
  CUSTOM_ERROR_MESSAGES,
  DROPDOWN_INITIAL_ITEM,
  CHOICES_MAP,
  FATAL_ERROR,
  MOVE_CONF_MSG,
  COMMENT_MAP,
} from 'utils/constant';
import { checkEkiCode } from 'utils/api';
import Measure from 'utils/measure';

export const scrollTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  });
};

export const isFatalApiError = (e) => {
  if(e.message === FATAL_ERROR) return true;
  if(e.message === 'Network Error') return true;
  if(e.response){
    if(e.response.status === 500) return true;
    if(e.response.status === 503) return true;
  }
  console.log('isFatalApiError: safe: ', { e });
  return false;
};

// date型を文字列に変換
export const changeStr = (date) => {
  if(!date) return '';

  const yearStr = date.getFullYear();
  let monthStr = 1 + date.getMonth();
  let dayStr = date.getDate();
    
  monthStr = (`0${ monthStr}`).slice(-2);
  dayStr = (`0${ dayStr}`).slice(-2);
    
  let formatStr = 'YYYY/MM/DD';
  formatStr = formatStr.replace(/YYYY/g, yearStr);
  formatStr = formatStr.replace(/MM/g, monthStr);
  formatStr = formatStr.replace(/DD/g, dayStr);
  return formatStr;
};

// 選択肢名と値を渡すと、ラベルを返却
export const getChoiceLabel = (choiceName, value) => {
  if(!value) return '';

  const choices = CHOICES_MAP[choiceName];
  const target = choices.filter(choice => choice.value === value )[0];
  return target ? target.label : '';
};
// 選択肢名とパターンを渡すと、ラベルを返却
export const getChoicePatternLabel = (choiceName, pattern) => {
  if(!pattern) return '';

  const choices = CHOICES_MAP[choiceName];
  const target = choices.filter(choice => choice.pattern === pattern )[0];
  return target ? target.label : '';
};

// 選択肢名を渡すと、取りうる値を返却
export const getChoiceValues = (choiceName) => {
  const choices = CHOICES_MAP[choiceName];
  return choices.map(choice => choice.value);
};

// 半角数字10桁の乱数を生成
export const randomID = () =>{
  const min = 1000000000 ;
  const max = 9999999999 ;
  const result = Math.floor( Math.random() * (max + 1 - min) ) + min;
  return `${result}`;

};
// 入力チェックのスキーマ
const ajv = new Ajv({ allErrors: true });
// エラーオブジェクト
export class ErrorMessage {
  constructor(target, message, level = 9){
    this.target = target;
    this.message = message;
    this.level = level;
  }
}
// ドロップダウンの正規表現生成（完全一致）
const makeChoiceRegStr = (choiceName) => {
  // 初期値は空なので選択不可
  const okList = getChoiceValues(choiceName).filter(val => val !== DROPDOWN_INITIAL_ITEM.value);
  return `^(${okList.join('|')})$`;
};
// 先読み否定一致（1つでも含んでいたらNG）
const makeNotIncludeRegStr = (notIncludeList) => {
  return `^(?![\\s\\S]*(${notIncludeList.join('|')})).*`;
};
const PATTERN_MAP = {
  xss: makeNotIncludeRegStr([ '<', '>', '\'', '"' ]),
};
// ajv の入力チェックルール
// https://github.com/ajv-validator/ajv/blob/master/docs/json-schema.md
const schemas = {
  // ページごとのajvの入力チェック
  [INPUT_URL]: {
    required: [ 'workCategory', 'companyNameText', 'dept', 'chargePersonNameText', 'tel', 'email', 'address', 'ekiCd', 'ekiName' ],
    properties: {
      workCategory: { type: 'string', pattern: makeChoiceRegStr('workCategoryList') },
      workCategorySub: {
        type: 'array',
        items: {
          type: 'string', 
          pattern: makeChoiceRegStr('workCategorySubList'),
        },
        uniqueItems: true,
      },
      workCategoryOtherDetail: { type: 'string', maxLength: 500, pattern: PATTERN_MAP.xss },
      workDetailText: { type: 'string', maxLength: 500, pattern: PATTERN_MAP.xss },
      employmentPlacementDispatching: { type: 'boolean' },
      companyNameText: { type: 'string', maxLength: 300, minLength: 1, pattern: PATTERN_MAP.xss },
      dept: { type: 'string', maxLength: 150, minLength: 1, pattern: PATTERN_MAP.xss },
      chargePersonNameText: { type: 'string', maxLength: 150, minLength: 1, pattern: PATTERN_MAP.xss },
      tel: { type: 'string', pattern: '^([0-9-]{10,13})$', minLength: 1 },
      email: { type: 'string', pattern: '[\\w\\-._]+@[\\w\\-._]+\\.[A-Za-z]+', minLength: 1 },
      address: { type: 'string', maxLength: 300, minLength: 1, pattern: PATTERN_MAP.xss },
      ekiCd: { type: 'string', pattern: '^[0-9]{5}$' },
      ekiName: { type: 'string', minLength: 1, pattern: PATTERN_MAP.xss },
    },
    showErrorMessages: [ 'workCategoryOtherDetail', 'workDetailText', 'companyNameText', 'dept', 'chargePersonNameText', 'tel', 'email', 'address', 'ekiName' ],
  },
  [CONF_URL]: {
    properties: {
    },
    showErrorMessages: [
    ],
  },
  [ADD_INPUT_URL]: {
    properties: {
      workInfoContent: {
        type: 'array',
        items: {
          type: 'string',
          pattern: makeChoiceRegStr('workInfoContentList'),
        },
        minItems: 1,
        uniqueItems: true,
      },
      skillLevel: { type: 'string', pattern: makeChoiceRegStr('skillLevelList') },
      bgdOfRecruitment: { type: 'string', pattern: makeChoiceRegStr('bgdOfRecruitmentList') },
      workStyle: { type: 'string', pattern: makeChoiceRegStr('workStyleList') },
      startTime: { type: 'string', pattern: makeChoiceRegStr('startTimeList') },
      endTime: { type: 'string', pattern: makeChoiceRegStr('endTimeList') },
      overTime: { type: 'string', pattern: makeChoiceRegStr('overTimeList') },
      desiredStartDateKubun: { type: 'string', pattern: makeChoiceRegStr('desiredStartDateKubunList') },
      workPeriod: { type: 'string', pattern: makeChoiceRegStr('workPeriodList') },
      workInfoContentCmnt: { type: 'string', maxLength: 500, pattern: PATTERN_MAP.xss },
      skillOtherCmnt: { type: 'string', maxLength: 500, pattern: PATTERN_MAP.xss },
      bgdOfRecruitmentCmnt: { type: 'string', maxLength: 500, pattern: PATTERN_MAP.xss },
      workStyleCmnt: { type: 'string', maxLength: 500, pattern: PATTERN_MAP.xss },
      startTimeCmnt: { type: 'string', maxLength: 500, pattern: PATTERN_MAP.xss },
      endTimeCmnt: { type: 'string', maxLength: 500, pattern: PATTERN_MAP.xss },
      workPeriodCmnt: { type: 'string', maxLength: 500, pattern: PATTERN_MAP.xss },
      useCar: { type: 'string', pattern: makeChoiceRegStr('useCarList') },
      holidayWork: { type: 'string', pattern: makeChoiceRegStr('holidayWorkList') },
      timeSavingAva: { type: 'string', pattern: makeChoiceRegStr('timeSavingAvaList') },
      remoteWork: { type: 'string', pattern: makeChoiceRegStr('remoteWorkList') },
      smokeEnvi: { type: 'string', pattern: makeChoiceRegStr('smokeEnviList') },
      welfare: {
        type: 'array',
        items: {
          type: 'string',
          pattern: makeChoiceRegStr('welfareList'),
        },
        minItems: 1,
        uniqueItems: true,
      },
    },
    allOf: [
      { if: { properties: { 'workInfoContent': { contains: {const: COMMENT_MAP.workInfoContentCmnt.value} } } }, then: { properties: { 'workInfoContentCmnt': { minLength: 1 } } } },//未入力
      { if: { properties: { 'workInfoContent': { contains: {const: COMMENT_MAP.workInfoContentCmnt.value} } } }, then: { properties: { 'workInfoContentCmnt': { pattern: '\\S' }  } } },//空白文字のみ
      { if: { properties: { 'skillOtherCmnt': { minLength: 1 } } }, then: { properties: { 'skillOtherCmnt': { pattern: '\\S' } } } },
      { if: { properties: { 'bgdOfRecruitment': { const: COMMENT_MAP.bgdOfRecruitmentCmnt.value } } }, then: { properties: { 'bgdOfRecruitmentCmnt': { minLength: 1 } } } },
      { if: { properties: { 'bgdOfRecruitment': { const: COMMENT_MAP.bgdOfRecruitmentCmnt.value } } }, then: { properties: { 'bgdOfRecruitmentCmnt': { pattern: '\\S' } } } },
      { if: { properties: { 'workStyle': { const: COMMENT_MAP.workStyleCmnt.value } } }, then: { properties: { 'workStyleCmnt': { minLength: 1 } } } },
      { if: { properties: { 'workStyle': { const: COMMENT_MAP.workStyleCmnt.value } } }, then: { properties: { 'workStyleCmnt': { pattern: '\\S' } } } },
      { if: { properties: { 'startTime': { const: COMMENT_MAP.startTimeCmnt.value } } }, then: { properties: { 'startTimeCmnt': { minLength: 1 } } } },
      { if: { properties: { 'startTime': { const: COMMENT_MAP.startTimeCmnt.value } } }, then: { properties: { 'startTimeCmnt': { pattern: '\\S' } } } },
      { if: { properties: { 'endTime': { const: COMMENT_MAP.endTimeCmnt.value } } }, then: { properties: { 'endTimeCmnt': { minLength: 1 } } } },
      { if: { properties: { 'endTime': { const: COMMENT_MAP.endTimeCmnt.value } } }, then: { properties: { 'endTimeCmnt': { pattern: '\\S' } } } },
      { if: { properties: { 'workPeriod': { const: COMMENT_MAP.workPeriodCmnt.value } } }, then: { properties: { 'workPeriodCmnt': { minLength: 1 } } } },
      { if: { properties: { 'workPeriod': { const: COMMENT_MAP.workPeriodCmnt.value } } }, then: { properties: { 'workPeriodCmnt': { pattern: '\\S' } } } },
      { if: { properties: { 'remoteWork': { const: '01' } } }, then: { properties: { 'remoteWorkCount': { pattern: makeChoiceRegStr('remoteWorkCountList') } } } },
    ],
    showErrorMessages: [
      'workInfoContentCmnt', 'skillOtherCmnt', 'bgdOfRecruitmentCmnt', 'workStyleCmnt', 'startTimeCmnt', 'endTimeCmnt', 'workPeriodCmnt'
    ],
  },
  [`${ADD_CONF_URL}:modal-HourSalary`]: {
    properties: {
      _modalSalaryMin: { type: 'string', minLength: 1, pattern: '^[0-9]{4}$' },
      _modalSalaryMax: { type: 'string', minLength: 1, pattern: '^[0-9]{4}$' },
    },
    showErrorMessages: [
      '_modalSalaryMin',
      '_modalSalaryMax',
    ],
  },
  [`${ADD_CONF_URL}:modal-workInfo`]: {
    properties: {
      _modalWorkInfo: { type: 'string',  minLength: 1,maxLength: 1500, pattern: PATTERN_MAP.xss },
    },
    allOf: [
      { properties: { '_modalWorkInfo': { pattern: '\\S' } } },
    ],
    showErrorMessages: [
      '_modalWorkInfo',
    ],
  },
  [`${ADD_CONF_URL}:modal-otherSkill`]: {
    properties: {
      _modalOtherSkill: { type: 'string', maxLength: 500, pattern: PATTERN_MAP.xss },
    },
    allOf: [
      { if: { properties: { '_modalOtherSkill': { minLength: 1 } } }, then: { properties: { '_modalOtherSkill': { pattern: '\\S' } } } },
    ],
    showErrorMessages: [
      '_modalOtherSkill',
    ],
  },
  [`${ADD_CONF_URL}:modal-workTime`]: {
    properties: {
      _modalWorkTime: { type: 'string', minLength: 1,maxLength: 1500, pattern: PATTERN_MAP.xss },
    },
    allOf: [
      { properties: { '_modalWorkTime': { pattern: '\\S' } } },
    ],
    showErrorMessages: [
      '_modalWorkTime',
    ],
  },
  [`${ADD_CONF_URL}:modal-workEnvi`]: {
    properties: {
      _modalWorkEnvi: { type: 'string', maxLength: 500, pattern: PATTERN_MAP.xss },
    },
    allOf: [
      { if: { properties: { '_modalWorkEnvi': { minLength: 1 } } }, then: { properties: { '_modalWorkEnvi': { pattern: '\\S' } } } },
    ],
    showErrorMessages: [
      '_modalWorkEnvi',
    ],
  },
  [`${ADD_CONF_URL}:modal-workPeriod`]: {
    properties: {
      _selectWorkPeriod: { type: 'string',  minLength: 1,maxLength: 500, pattern: PATTERN_MAP.xss },
    },
    allOf: [
      { properties: { '_selectWorkPeriod': { pattern: '\\S' } } },
    ],
    showErrorMessages: [
      '_selectWorkPeriod',
    ],
  },

  [`${ADD_CONF_URL}:modal-dayOfTheWeek`]: {
    properties: {
      _selectDayOfTheWeek: { type: 'string',  minLength: 1,maxLength: 500, pattern: PATTERN_MAP.xss },
    },
    allOf: [
      { properties: { '_selectDayOfTheWeek': { pattern: '\\S' } } },
    ],
    showErrorMessages: [
      '_selectDayOfTheWeek',
    ],
  },

  [`${ADD_CONF_URL}:modal-bgdOfRecruitmentCmnt`]: {
    properties: {
      _selectBgdOfRecruitmentCmnt: { type: 'string', maxLength: 500, pattern: PATTERN_MAP.xss },
    },
    allOf: [
      { if: { properties: { '_selectBgdOfRecruitment': { const: COMMENT_MAP.bgdOfRecruitmentCmnt.value } } }, then: { properties: { '_selectBgdOfRecruitmentCmnt': { minLength: 1 } } } },
      { if: { properties: { '_selectBgdOfRecruitment': { const: COMMENT_MAP.bgdOfRecruitmentCmnt.value } } }, then: { properties: { '_selectBgdOfRecruitmentCmnt': { pattern: '\\S' } } } },
    ],
    showErrorMessages: [
      '_selectBgdOfRecruitmentCmnt',
    ],
  },
  [`${ADD_CONF_URL}:modal-welfare`]: {
    properties: {
      _selectWelfare: {
        type: 'array',
        items: {
          type: 'string',
          pattern: makeChoiceRegStr('welfareList'),
        },
        minItems: 1,
        uniqueItems: true,
      },
    },
    allOf: [
      { if: { properties: { '_selectWelfare': { minLength: 1 } } }, then: { properties: { '_selectWelfare': { pattern: '\\S' } } } },
    ],
    showErrorMessages: [
      '_selectWelfare',
    ],
  },


  [ADD_CONF_URL]: {
    required: [],
    properties: {
      // モーダルに変更する項目チェック
      _selectDateKubun: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('desiredStartDateKubunList') } },
      _selectDate: { type: 'string', if: { maxLength: 0 }, else: { pattern: PATTERN_MAP.xss } },
      _selectWorkPeriod: { type: 'string', if: { maxLength: 0 }, else: {  pattern: PATTERN_MAP.xss } },
      _selectDayOfTheWeek: { type: 'string',  minLength: 1,maxLength: 500, pattern: PATTERN_MAP.xss },
      _selectOverTimeMin: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('overTimeHourList') } },
      _selectOverTimeMax: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('overTimeHourList') } },
      _selectExcel: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('skillLevelExcelConfList') } },
      _selectPowerpoint: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('skillLevelPowerpointConfList') } },
      otherComment: { type: 'string', maxLength: 500, minLength: 0, if: { maxLength: 0 }, else: { pattern: PATTERN_MAP.xss } },
      
      _selectBgdOfRecruitment: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('bgdOfRecruitmentList') } },
      _selectRemoteWork: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('remoteWorkList') } },
      _selectRemoteWorkCount: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('remoteWorkCountList') } },
      _selectHolidayWork: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('holidayWorkList') } },
      _selectTimeSavingAva: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('timeSavingAvaList') } },
      _selectUseCar: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('useCarList') } },
      _selectSmokeEnvi: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('smokeEnviList') } },
      _selectWelfare: {
        type: 'array',
        items: {
          type: 'string',
          pattern: makeChoiceRegStr('welfareList'),
        },
        minItems: 1,
        uniqueItems: true,
      },
    },
    allOf: [
      { if: { properties: { 'remoteWork': { const: '01' } } }, then: { properties: { 'remoteWorkCount': { pattern: makeChoiceRegStr('remoteWorkCountList') } } } },
      { if: { properties: { 'otherComment': { minLength: 1 } } }, then: { properties: { 'otherComment': { pattern: '\\S' } } } },
    ],
    showErrorMessages: [
      'workTime',
      'overTime',
      'otherComment',
    ],
  },

  // Post時に変換するパラメータのみ追加チェック
  [`${ADD_CONF_URL}:post`]: {
    required: [],
    properties: {
      // state項目チェック
      desiredStartDateKubun: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('desiredStartDateKubunList') } },
      desiredStartDate: { type: 'string', if: { maxLength: 0 }, else: { pattern: PATTERN_MAP.xss } },
      timeSavingAva: { type: 'string', pattern: makeChoiceRegStr('timeSavingAvaList') },
      overtimeHoursMin: { type: 'integer', minimum: 0, maximum: 45 },
      overtimeHoursMax: { type: 'integer', minimum: 0, maximum: 45 },
      hourSalaryMin: { type: 'integer', minimum: 0, maximum: 9999 },
      hourSalaryMax: { type: 'integer', minimum: 0, maximum: 9999 },
      strWorkInfo: { type: 'string', if: { maxLength: 0 }, else: { pattern: PATTERN_MAP.xss } },
      skillLevelExcel: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('skillLevelExcelConfList') } },
      skillLevelPowerpoint: { type: 'string', if: { maxLength: 0 }, else: { pattern: makeChoiceRegStr('skillLevelPowerpointConfList') } },
      workEnvi: { type: 'string', if: { maxLength: 0 }, else: { pattern: PATTERN_MAP.xss } },
      strOtherSkill: { type: 'string', if: { maxLength: 0 }, else: { pattern: PATTERN_MAP.xss } },
      strWorkPeriod: { type: 'string', if: { maxLength: 0 }, else: { pattern: PATTERN_MAP.xss } },
      strDayOfTheWeek: { type: 'string', if: { maxLength: 0 }, else: { pattern: PATTERN_MAP.xss } },
      strWorkTime: { type: 'string', if: { maxLength: 0 }, else: { pattern: PATTERN_MAP.xss } },
      bgdOfRecruitment: { type: 'string', pattern: makeChoiceRegStr('bgdOfRecruitmentList') },
      bgdOfRecruitmentCmnt: { type: 'string', if: { maxLength: 0 }, else: { pattern: PATTERN_MAP.xss } },
      otherComment: { type: 'string', maxLength: 500, minLength: 0, if: { maxLength: 0 }, else: { pattern: PATTERN_MAP.xss } },
      checkPointRemoteWork: { type: 'string', pattern: makeChoiceRegStr('remoteWorkList') },
      checkPointHolidayWork: { type: 'string', pattern: makeChoiceRegStr('holidayWorkList') },
      checkPointUseCar: { type: 'string', pattern: makeChoiceRegStr('useCarList') },
      checkPointSmokeEnvi: { type: 'string', pattern: makeChoiceRegStr('smokeEnviList') },
      checkPointWelfare: {
        type: 'array',
        items: {
          type: 'string',
          pattern: makeChoiceRegStr('welfareList'),
        },
        minItems: 0,
        uniqueItems: true,
      },
      email: { type: 'string', pattern: '[\\w\\-._]+@[\\w\\-._]+\\.[A-Za-z]+', minLength: 1 },
      companyNameText: { type: 'string', maxLength: 300, minLength: 1, pattern: PATTERN_MAP.xss },
    },
    allOf: [
      { if: { properties: { 'checkPointRemoteWork': { const: '01' } } }, then: { properties: { 'checkPointRemoteWorkCount': { pattern: makeChoiceRegStr('remoteWorkCountList') } } } },
   ],
    showErrorMessages: [
      'otherComment',
    ],
  },
};

// ajv 以外の相関チェック、APIを利用したチェックなど
const customChecks = { 
  // 対象駅判定
  checkEkiCode: async ({ ekiCd }) => {
    const target = 'ekiCd';
    const ok = null ;
    const ng = new ErrorMessage(target, CUSTOM_ERROR_MESSAGES.checkEkiCodeNG, 1);

    if( !ekiCd || ekiCd === '') return ok;

    const valid = await checkEkiCode({ ekiCd })
      .catch(err => {
        console.log('checkEkiCode: catch');
        if(err.response.status === 400)return false;
        throw err;
      });
    return valid ? ok : ng;
  },
  // 就業開始希望日日付指定
  checkDesiredStartDate: async ({ desiredStartDate, desiredStartDateKubun }) => {
    const target = 'desiredStartDate';
    const ok = null ;
    const ng = new ErrorMessage(target, CUSTOM_ERROR_MESSAGES.checkDesiredStartDateNG, 1);

    // 正規表現パターン（半角英数４桁に一致）
    const regex = new RegExp(/^[0-9]{4}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/);
     
    // 判定
    if(desiredStartDateKubun === '02'){
      if (regex.test(desiredStartDate)) {
        return ok;
      } 
      return ng;
    } 
    return ok;
    
  },
  // 最大 - 最小 < 0
  checkLessThanZero: async ({ target, min, max }) => {
    const ok = null ;

    const name = CHOICES_NAME_MAP[target];
    const ng = new ErrorMessage(target, `${name}の入力内容が不正です`, 1);

    // いずれか未入力はOK
    if(!min || !max) return ok;

    const valid = (parseFloat(max) - parseFloat(min)) >= 0;
    if(!valid) return ng;

    if(target === '_modalSalaryMin'){
      const valid = parseFloat(max) < 1000 || parseFloat(min) < 1000;
      if(valid) return ng;
    }
    return ok;
  },
  checkWorkCategoryRequire: async ({ workCategory, workCategorySub })=> {
    const ok = null;
    const target = 'workCategorySub';
    const ng = new ErrorMessage(target, null, 1);
    if(workCategory === 'A' || workCategory === 'B' || workCategory === 'C'){
      if(!workCategorySub.length){
        return ng;
      }
    }
    return ok;
  },
  checkRemoteWorkCount: async ({ remoteWork, remoteWorkCount }) => {
    const target = 'remoteWorkCount';
    const ok = null;
    const ng = new ErrorMessage(target, CUSTOM_ERROR_MESSAGES.remoteWorkCountNG, 1);
    if (remoteWork === '01') {
      if (!remoteWorkCount.length) {
        return ng;
      }
    }
    return ok;
  },
};

// エラーをレベルでソート(0: 最優先)
export const sortErrors = (errors) => {
  errors.sort((a, b) => {
    if ( a.level === b.level ) { return 0; }
    return a.level < b.level ? -1 : 1;
  });
  return errors;
};

// 各画面でのvalidation
// カスタムチェック
export const isCustomValid = async ({ checks: checkFuncNames, target }) => {

  // カスタムエラーチェック
  const errorResult = await Promise.all(
    checkFuncNames.map(async (name) => {
      const ret = await customChecks[name]({ ...target })
        .catch(err => {
          console.log('customChecks');
          throw err;
        });

      return ret ? {
        ...ret,
        custom: true,
      } : null;
    }),
  ).catch(err => {
    console.log('Promise.all');
    throw err;
  });
  const customErrorMessages = errorResult.filter(x => !!x);
  const customErrorValid = customErrorMessages.length === 0;
  
  // エラーを返却
  return [
    customErrorValid,
    customErrorMessages,
  ];
};
// schema による静的チェック
export const isSchemaValid = ({ path = window.location.pathname, target }) => {

  // チェックルールを取得
  const schema = schemas[path];
  if(!schema) return [ true, [] ];

  // ajvによるチェック
  const validate = ajv.compile(schema);
  const valid = validate(target);
  // ajvによるエラー
  const errors = validate.errors ? validate.errors : [];
  const errorMessages = errors
    .map(error => {
      const dataPath = error.dataPath.replace(/^\./, '');
      const ok = null;
      // エラーメッセージ表示対象のみ表示
      if(!schema.showErrorMessages.includes(dataPath)) {
        return ok;
      }

      // エラーメッセージが存在するエラーのみ表示
      const message = ERROR_MESSAGES.filter(e => {
        return !!error.message.match(e.pattern);
      })[0];
      if(message.length === 0) {
        return ok;
      }

      // エラーメッセージを返却
      const name = CHOICES_NAME_MAP[dataPath];
      return new ErrorMessage(dataPath, `${name}${message.label}`, 0);

    }).filter(x => !!x);

  console.log({ errorMessages });
  
  // NGの場合のコンソール表示
  if(!valid){
    console.log(
      '%c[DEFAULT VALIDATE_ERROR]',
      'color:blue;',
    );
    console.log({
      path,
      target,
      schema,
      valid,
      validate,
      erros: errorMessages,
    });
  }

  // エラーを返却
  return [
    valid,
    errorMessages,
  ];
};

// Web計測を実施
export const useAnalytics = () => {
  const isAnalytics = env.IS_ANALYTICS;
  const location = useLocation();

  useEffect(()=>{
    if( !isAnalytics ) return;
    
    switch(location.pathname){
      case INPUT_URL:
        Measure({
          pageName: 'sol:clcd:odform:input:index.html',
          title: '派遣依頼フォーム',
        }, false);
        break;
      case CONF_URL:
        Measure({
          pageName: 'sol:clcd:odform:conf:index.html',
          title: '派遣依頼確認画面',
        }, false);
        break;
      case THANKS_URL:
        Measure({
          pageName: 'sol:clcd:odform:thanks:index.html',
          title: '派遣依頼完了画面',
        }, false);
        break;
      case ADD_INPUT_URL:
        Measure({
          pageName: 'sol:clcd:odform:AddInput:index.html',
          title: 'オンライン発注入力画面',
        }, false);
        break;
      case ADD_CONF_URL:
        Measure({
          pageName: 'sol:clcd:odform:AddConf:index.html',
          title: 'オンライン発注確認画面',
        }, false);
        break;
      case ADD_THANKS_URL:
        Measure({
          pageName: 'sol:clcd:odform:AddThanks:index.html',
          title: 'オンライン発注完了画面',
        }, false);
        break;
      default:
        break;
    }
  }, [ isAnalytics, location.pathname ] );
};

// 直接遷移を確認
export const useDirectTransCheck = ({ state }) => {
  const path = window.location.pathname;
  const { prevUrl } = PAGE_MAP[path];
  const { validMap } = state;

  const redirectUrl = env.ENV === 'local' ? INPUT_URL : LP_URL;

  useEffect(()=> {
    if(prevUrl && !validMap[prevUrl]){
      console.log('[REDIRECT] useDirectTransCheck');
      window.location.href = redirectUrl;
    } 
  }, [ prevUrl, redirectUrl, validMap ]);
};
 
// window 幅、高さを取得
export const useWindowDimensions = () => {
 
  const getWindowDimensions = () => {
    const {
      innerHeight: height,
    } = window;
    const {
      offsetWidth: width,
    } = window.document.body;

    return {
      width,
      height,
    };
  };
 
  const [ windowDimensions, setWindowDimensions ] = useState(getWindowDimensions());
  useEffect(() => {
    const onResize = () => {
      setWindowDimensions(getWindowDimensions());
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return windowDimensions;
};
// UAがIEかどうか判定
export const useIECheck = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIE = userAgent.indexOf('msie') !== -1 || userAgent.indexOf('trident') !== -1;
  return isIE;
};

// 画面遷移確認ダイアログ
export const MoveCheckMsg = (e) => {
  e.preventDefault();
  e.returnValue = MOVE_CONF_MSG;
};
export const MoveCheckStart = () => {
  window.addEventListener('beforeunload', MoveCheckMsg);
  console.log('addEventListener beforeunload');
};
export const MoveCheckStop = () => {
  window.removeEventListener('beforeunload', MoveCheckMsg);
  console.log('removeEventListener beforeunload');
};

export const postHeightToParent = (origin, contHeight) => {
  const stage = process.env.REACT_APP_STAGE || 'local';
  const path = window.location.pathname;
  let postPrigin = '*';
  if(stage === 'main' || stage === 'staging'){
    postPrigin = origin;
  }
  window.parent.postMessage({ action: 'adjustHeight', height: contHeight }, postPrigin);
  if(path !== INPUT_URL){
    window.parent.postMessage({ action: 'scrollTo', scrollTarget: '.p-form' }, postPrigin);
  }
};

export const getCookies = (key) => {
  let cookie = document.cookie.match("(^|;)\\s*" + key + "\\s*=\\s*([^;]+)");
  return cookie ? cookie.pop() : "";
};